import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@repo/blacksmith-ui/dialog";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useIsMac } from "@/hooks/useIsMac";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useActiveApp } from "@/components/sidebar/useActiveApp";
interface ShortcutsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
interface Shortcut {
  keys: string[];
  description: string;
  alt?: string[];
}
interface ShortcutSection {
  category: string;
  items: Shortcut[];
}
const getShortcuts = (isMac: boolean, {
  includeTests,
  includeCodesmith,
  includeRailLetters
}: {
  includeTests: boolean;
  includeCodesmith: boolean;
  includeRailLetters: boolean;
}): ShortcutSection[] => {
  const mod = isMac ? "Cmd" : "Ctrl";
  const sections: ShortcutSection[] = [{
    category: "Navigation",
    items: [...(includeRailLetters ? [{
      keys: ["h"],
      description: "Home"
    }, {
      keys: ["a"],
      description: "GitHub Actions"
    }] : []), ...(includeRailLetters && includeCodesmith ? [{
      keys: ["p"],
      description: "Proof"
    }] : []), {
      keys: ["g", "w"],
      description: "Workflows"
    }, {
      keys: ["g", "j"],
      description: "Jobs"
    }, {
      keys: ["g", "l"],
      description: "Logs"
    }, {
      keys: ["g", "a"],
      description: "Analytics"
    }, {
      keys: ["g", "d"],
      description: "Docker Analytics"
    }, {
      keys: ["g", "s"],
      description: "Sticky Disks"
    }, {
      keys: ["g", "m"],
      description: "Monitors"
    }, {
      keys: ["g", "u"],
      description: "Usage & Billing"
    }, {
      keys: ["g", "t"],
      description: "Settings"
    }]
  }];
  if (includeTests) {
    sections.push({
      category: "Tests",
      items: [{
        keys: ["j"],
        description: "Next test/suite",
        alt: ["↓"]
      }, {
        keys: ["k"],
        description: "Previous test/suite",
        alt: ["↑"]
      }, {
        keys: ["Enter"],
        description: "Select test or toggle suite",
        alt: ["←"]
      }, {
        keys: ["/"],
        description: "Focus search"
      }, {
        keys: ["f"],
        description: "Open filter menu"
      }]
    });
  }
  sections.push({
    category: "Interface",
    items: [{
      keys: [mod, "K"],
      description: "Open command palette"
    }, {
      keys: [mod, "B"],
      description: "Toggle sidebar"
    }, {
      keys: [mod, "/"],
      description: "Toggle all panels"
    }, {
      keys: ["?"],
      description: "Show shortcuts"
    }]
  });
  if (includeCodesmith) {
    sections.push({
      category: "[code]smith",
      items: [...(includeRailLetters ? [{
        keys: ["c"],
        description: "Open [code]smith"
      }] : []), {
        keys: [isMac ? "Option" : "Alt", "Shift", "N"],
        description: "New chat"
      }, {
        keys: [isMac ? "Option" : "Alt", "B"],
        description: "Toggle chat list"
      }, {
        keys: [isMac ? "Option" : "Alt", mod, "B"],
        description: "Toggle workspace panel"
      }, {
        keys: [isMac ? "Option" : "Alt", "L"],
        description: "Focus chat input"
      }, {
        // A single "Option ×2" badge: two badges would render as
        // "Option + Option", which reads as a simultaneous two-key chord.
        keys: [isMac ? "Option ×2" : "Alt ×2"],
        description: "Session switcher (double-tap and hold, J/K to move, release to switch)",
        alt: [isMac ? "Option" : "Alt", "J/K"]
      }, {
        keys: [isMac ? "Option" : "Alt", "D"],
        description: "Archive current session"
      }, {
        keys: [isMac ? "Option" : "Alt", "G"],
        description: "Open session's PRs on GitHub"
      }, {
        keys: [isMac ? "Option" : "Alt", "P"],
        description: "Open session's PR preview environments"
      }, {
        keys: ["Shift", mod, "Enter"],
        description: "Queue message (Enter steers while [code]smith is busy)",
        alt: [isMac ? "Option" : "Alt", "Shift", "Enter"]
      }, {
        keys: [mod, "Shift", "."],
        description: "Toggle Fast/Deep mode"
      }]
    });
  }
  return sections;
};
function KeyBadges(t0) {
  const $ = _c(2);
  const {
    keys
  } = t0;
  let t1;
  if ($[0] !== keys) {
    t1 = <ShortcutKeys keys={keys} kbdClassName="h-5 min-w-5" />;
    $[0] = keys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ShortcutsModal(t0) {
  const $ = _c(34);
  const {
    open,
    onOpenChange
  } = t0;
  const isMac = useIsMac();
  const router = useRouter();
  const activeApp = useActiveApp();
  const {
    codesmithEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== activeApp || $[1] !== codesmithEnabled || $[2] !== isMac || $[3] !== onOpenChange || $[4] !== open || $[5] !== router.pathname) {
    const isTestPage = router.pathname.includes("[runId]");
    const shortcuts = getShortcuts(isMac, {
      includeTests: isTestPage,
      includeCodesmith: codesmithEnabled ?? false,
      includeRailLetters: activeApp !== "codesmith"
    });
    const leftColumn = shortcuts.filter(_temp);
    const rightColumn = shortcuts.filter(_temp2);
    const renderSection = _temp4;
    T1 = Dialog;
    t7 = open;
    t8 = onOpenChange;
    T0 = DialogContent;
    t5 = "max-w-3xl";
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <DialogHeader><DialogTitle>Keyboard shortcuts</DialogTitle></DialogHeader>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t3 = "grid grid-cols-2 gap-x-10";
    t4 = <div className="space-y-6">{leftColumn.map(renderSection)}</div>;
    t1 = "space-y-6";
    t2 = rightColumn.map(renderSection);
    $[0] = activeApp;
    $[1] = codesmithEnabled;
    $[2] = isMac;
    $[3] = onOpenChange;
    $[4] = open;
    $[5] = router.pathname;
    $[6] = T0;
    $[7] = T1;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    T0 = $[6];
    T1 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t2) {
    t9 = <div className={t1}>{t2}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t9) {
    t10 = <div className={t3}>{t4}{t9}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== T0 || $[25] !== t10 || $[26] !== t5 || $[27] !== t6) {
    t11 = <T0 className={t5}>{t6}{t10}</T0>;
    $[24] = T0;
    $[25] = t10;
    $[26] = t5;
    $[27] = t6;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== T1 || $[30] !== t11 || $[31] !== t7 || $[32] !== t8) {
    t12 = <T1 open={t7} onOpenChange={t8}>{t11}</T1>;
    $[29] = T1;
    $[30] = t11;
    $[31] = t7;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp4(section) {
  return <div key={section.category}><h3 className="text-sm font-medium text-muted-foreground mb-3">{section.category}</h3><div className="space-y-2">{section.items.map(_temp3)}</div></div>;
}
function _temp3(shortcut, index) {
  return <div key={index} className="flex items-start justify-between gap-x-4 py-1"><span className="min-w-0 whitespace-pre-line text-sm">{shortcut.description}</span><div className="flex flex-wrap items-center justify-end gap-1"><KeyBadges keys={shortcut.keys} />{shortcut.alt && <span className="flex items-center gap-1"><span className="text-xs text-muted-foreground mx-1">or</span><KeyBadges keys={shortcut.alt} /></span>}</div></div>;
}
function _temp2(s_0) {
  return s_0.category === "Interface" || s_0.category === "[code]smith";
}
function _temp(s) {
  return s.category === "Navigation" || s.category === "Tests";
}